import type { ReactNode } from "react";
import { type JSX, createContext, useContext } from "react";
import type { StyleProps } from "@chakra-ui/react";
import { Flex, Text, Box } from "@chakra-ui/react";
import type { SVGFillerImage } from "./svgFillerTypes";
import { Button as V2Button, type ButtonProps } from "components/button/Button";
import { Icon, type Icons } from "../icon/Icon";
import { isNil } from "es-toolkit/compat";
import { Illustration } from "components/illustration/Illustration";
export const IMAGE_SIZE = ["large", "medium", "small"] as const;

const SVGFillerContext = createContext<any>({ isOther: false });

export type ImageSize = (typeof IMAGE_SIZE)[number];

interface FillerProps extends StyleProps {
  imagePath?: SVGFillerImage;
  headline?: string;
  children?: ReactNode;
}

export interface SVGFillerProps extends FillerProps {
  illustration?: ReactNode;
  variant?: "empty" | "other";
  headlineFontSize?: string;
}

type FillerButtonProps = Omit<
  ButtonProps,
  "children" | "icon" | "size" | "variant"
> & {
  iconName?: Icons | null;
  [x: string]: any;
};

interface InternalFillerProps extends FillerProps {
  variant?: "horizontal" | "vertical";
  text?: string | JSX.Element;
  imageHeight?: string;
  imageWidth?: string;
}

type InternalCTAProps = Omit<ButtonProps, "size" | "variant">;

export namespace Filler {
  export const SVGFiller = ({
    variant = "empty",
    imagePath = "error/Triangle",
    headline = "Not found",
    headlineFontSize,
    illustration,
    children,
    ...rest
  }: SVGFillerProps) => {
    const isOther = variant === "other";

    return (
      <Flex
        direction="column"
        gap={isOther ? "6" : "8"}
        maxWidth={isOther ? "287px" : { base: "100%", lg: "500px" }}
        margin="88px auto 0 auto"
        {...rest}
      >
        <Box
          height={
            isOther ? "200px" : { base: "40", lg: "200px", "3xl": "300px" }
          }
        >
          {illustration || (
            <Illustration
              name={imagePath}
              height="100%"
              width="100%"
              data-cy="eon-empty-state" // Called 'eon-empty-state' b/c it used to be Eon SVGs.
            />
          )}
        </Box>

        <Flex direction="column" alignItems="center" gap={isOther ? "4" : "2"}>
          <Box
            textStyle={isOther ? "h3" : "h4"}
            textAlign="center"
            textColor="fg.default"
            fontSize={headlineFontSize}
          >
            {headline}
          </Box>
          {children && (
            <Box
              display="grid"
              rowGap={isOther ? "6" : "8"}
              gridTemplateRows={`min-content`}
              gridTemplateColumns="1fr"
              textStyle={isOther ? "base" : "subtitle4"}
              justifyContent="center"
              justifyItems={"center"}
              textAlign="center"
              color="fg.emphasized"
            >
              <SVGFillerContext.Provider value={{ isOther }}>
                {children}
              </SVGFillerContext.Provider>
            </Box>
          )}
        </Flex>
      </Flex>
    );
  };

  export const InternalFiller = ({
    variant = "vertical",
    imagePath = "error/Triangle",
    headline = "Not found",
    text,
    imageHeight,
    imageWidth,
    children,
    ...rest
  }: InternalFillerProps) => {
    const isVertical = variant === "vertical";

    return (
      <Flex
        direction={isVertical ? "column" : "row"}
        gap="6"
        alignItems="center"
        justifyContent={"flex-start"}
        {...rest}
      >
        <Box
          height={imageHeight ? imageHeight : "120px"}
          width={imageWidth ? imageWidth : "120px"}
        >
          <Illustration name={imagePath} height="100%" width="100%" />
        </Box>

        <Flex
          direction="column"
          gap="4"
          alignItems={isVertical ? "center" : "flex-start"}
          justifyContent="flex-start"
        >
          <Flex
            direction="column"
            gap="1"
            alignItems={isVertical ? "center" : "flex-start"}
            justifyContent="flex-start"
          >
            <Text
              as="span"
              textStyle="baseBold"
              color="fg.default"
              textAlign={isVertical ? "center" : "left"}
            >
              {headline}
            </Text>
            {text && (
              <Text
                as="span"
                color="fg.emphasized"
                textStyle="base"
                textAlign={isVertical ? "center" : "left"}
              >
                {text}
              </Text>
            )}
            {children && (
              <Flex direction="row" gap="6" color="fg.emphasized">
                {children}
              </Flex>
            )}
          </Flex>
        </Flex>
      </Flex>
    );
  };

  export const Button = ({
    children,
    iconName = "actions/Add/Add",
    variant = "primary",
    ...rest
  }: FillerButtonProps) => {
    const { isOther } = useContext(SVGFillerContext);
    return (
      <V2Button
        {...rest}
        variant={variant}
        margin="0"
        size={isOther ? "md" : "lg"}
        width={
          isOther
            ? { base: "fit-content" }
            : { base: "100%", sm: "fit-content" }
        }
        maxWidth={isOther ? {} : { base: "100%" }}
        icon={isNil(iconName) ? undefined : <Icon name={iconName} />}
      >
        {children}
      </V2Button>
    );
  };

  export const InternalCTA = ({ children, ...props }: InternalCTAProps) => (
    <V2Button variant="link" size="md" padding="0" {...props}>
      {children}
    </V2Button>
  );
}
